import { Action } from "redux";

import { CommentDeleteNormalizedResponse } from "@/mk2/apps/blogs/containers/Comments/Comments.actions";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";

/////////////////////////////////////////////////////////////////////
// Mark feed as read

export const GROUP_SHARED_FEED_READ = "GROUP_SHARED_FEED_READ";

export interface GroupSharedFeedReadAction extends Action {
    readonly type: typeof GROUP_SHARED_FEED_READ;
    readonly groupId: number;
}

export const groupSharedFeedRead = (groupId: number): GroupSharedFeedReadAction => ({
    type: GROUP_SHARED_FEED_READ,
    groupId,
});

/////////////////////////////////////////////////////////////////////
// Ask for a group membership

export interface GroupSharedAskForMembershipApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_REQUEST =
    "GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_REQUEST";

export interface GroupSharedAskForMembershipApiRequestAction
    extends GroupSharedAskForMembershipApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_REQUEST;
}

export const GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_SUCCESS =
    "GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_SUCCESS";

export interface GroupSharedAskForMembershipApiSuccessAction
    extends GroupSharedAskForMembershipApiAction, APISuccessAction {
    readonly type: typeof GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_SUCCESS;
}

export const GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_FAILURE =
    "GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_FAILURE";

export interface GroupSharedAskForMembershipApiFailureAction
    extends GroupSharedAskForMembershipApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_FAILURE;
}

export const groupSharedAskForMembershipApi = {
    request: (groupId: number): GroupSharedAskForMembershipApiRequestAction => ({
        type: GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_REQUEST,
        groupId,
    }),
    success: (groupId: number, response: any): GroupSharedAskForMembershipApiSuccessAction => ({
        type: GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_SUCCESS,
        groupId,
        response,
    }),
    failure: (groupId: number, error: string): GroupSharedAskForMembershipApiFailureAction => ({
        type: GROUP_SHARED_ASK_FOR_MEMBERSHIP_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_ASK_FOR_MEMBERSHIP = "GROUP_SHARED_ASK_FOR_MEMBERSHIP";

export interface GroupSharedAskForMembershipAction extends GroupSharedAskForMembershipApiAction {
    readonly type: typeof GROUP_SHARED_ASK_FOR_MEMBERSHIP;
}

export const groupSharedAskForMembership = (
    groupId: number,
): GroupSharedAskForMembershipAction => ({
    type: GROUP_SHARED_ASK_FOR_MEMBERSHIP,
    groupId,
});

/////////////////////////////////////////////////////////////////////
// Join group

export interface GroupSharedJoinGroupApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_JOIN_GROUP_API_REQUEST = "GROUP_SHARED_JOIN_GROUP_API_REQUEST";

export interface GroupSharedJoinGroupApiRequestAction
    extends GroupSharedJoinGroupApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_JOIN_GROUP_API_REQUEST;
}

export const GROUP_SHARED_JOIN_GROUP_API_SUCCESS = "GROUP_SHARED_JOIN_GROUP_API_SUCCESS";

export interface GroupSharedJoinGroupApiSuccessAction
    extends GroupSharedJoinGroupApiAction, APISuccessAction {
    readonly type: typeof GROUP_SHARED_JOIN_GROUP_API_SUCCESS;
    readonly memberships: number[];
}

export const GROUP_SHARED_JOIN_GROUP_API_FAILURE = "GROUP_SHARED_JOIN_GROUP_API_FAILURE";

export interface GroupSharedJoinGroupApiFailureAction
    extends GroupSharedJoinGroupApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_JOIN_GROUP_API_FAILURE;
}

export const groupSharedJoinGroupApi = {
    request: (groupId: number): GroupSharedJoinGroupApiRequestAction => ({
        type: GROUP_SHARED_JOIN_GROUP_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        memberships: number[],
        response: any,
    ): GroupSharedJoinGroupApiSuccessAction => ({
        type: GROUP_SHARED_JOIN_GROUP_API_SUCCESS,
        groupId,
        memberships,
        response,
    }),
    failure: (groupId: number, error: string): GroupSharedJoinGroupApiFailureAction => ({
        type: GROUP_SHARED_JOIN_GROUP_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_JOIN_GROUP = "GROUP_SHARED_JOIN_GROUP";

export interface GroupSharedJoinGroupAction extends GroupSharedJoinGroupApiAction {
    readonly type: typeof GROUP_SHARED_JOIN_GROUP;
    readonly origin: string;
}

export const groupSharedJoinGroup = (
    groupId: number,
    origin: string,
): GroupSharedJoinGroupAction => ({
    type: GROUP_SHARED_JOIN_GROUP,
    groupId,
    origin,
});

/////////////////////////////////////////////////////////////////////
// Leave group

export interface GroupSharedLeaveGroupApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_LEAVE_GROUP_API_REQUEST = "GROUP_SHARED_LEAVE_GROUP_API_REQUEST";

export interface GroupSharedLeaveGroupApiRequestAction
    extends GroupSharedLeaveGroupApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_LEAVE_GROUP_API_REQUEST;
}

export const GROUP_SHARED_LEAVE_GROUP_API_SUCCESS = "GROUP_SHARED_LEAVE_GROUP_API_SUCCESS";

export interface GroupSharedLeaveGroupApiSuccessAction
    extends GroupSharedLeaveGroupApiAction, APISuccessAction {
    readonly type: typeof GROUP_SHARED_LEAVE_GROUP_API_SUCCESS;
    readonly memberships: number[];
}

export const GROUP_SHARED_LEAVE_GROUP_API_FAILURE = "GROUP_SHARED_LEAVE_GROUP_API_FAILURE";

export interface GroupSharedLeaveGroupApiFailureAction
    extends GroupSharedLeaveGroupApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_LEAVE_GROUP_API_FAILURE;
}

export const groupSharedLeaveGroupApi = {
    request: (groupId: number): GroupSharedLeaveGroupApiRequestAction => ({
        type: GROUP_SHARED_LEAVE_GROUP_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        memberships: number[],
        response: any,
    ): GroupSharedLeaveGroupApiSuccessAction => ({
        type: GROUP_SHARED_LEAVE_GROUP_API_SUCCESS,
        groupId,
        memberships,
        response,
    }),
    failure: (groupId: number, error: string): GroupSharedLeaveGroupApiFailureAction => ({
        type: GROUP_SHARED_LEAVE_GROUP_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_LEAVE_GROUP = "GROUP_SHARED_LEAVE_GROUP";

export interface GroupSharedLeaveGroupAction extends GroupSharedLeaveGroupApiAction {
    readonly type: typeof GROUP_SHARED_LEAVE_GROUP;
    readonly redirect: boolean;
    onSuccess();
}

export const groupSharedLeaveGroup = (
    groupId: number,
    redirect: boolean,
    onSuccess = null,
): GroupSharedLeaveGroupAction => ({
    type: GROUP_SHARED_LEAVE_GROUP,
    groupId,
    redirect,
    onSuccess,
});

/////////////////////////////////////////////////////////////////////
// Set "hide (current group's) posts in (user's) feed" to true

export interface GroupSharedHidePostsOfAGroupInFeedApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_REQUEST =
    "GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_REQUEST";

export interface GroupSharedHidePostsOfAGroupInFeedApiRequestAction
    extends GroupSharedHidePostsOfAGroupInFeedApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_REQUEST;
}

export const GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_SUCCESS =
    "GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_SUCCESS";

export interface GroupSharedHidePostsOfAGroupInFeedApiSuccessAction
    extends GroupSharedHidePostsOfAGroupInFeedApiAction, APISuccessAction {
    readonly type: typeof GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_SUCCESS;
}

export const GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_FAILURE =
    "GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_FAILURE";

export interface GroupSharedHidePostsOfAGroupInFeedApiFailureAction
    extends GroupSharedHidePostsOfAGroupInFeedApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_FAILURE;
}

export const groupSharedHidePostsOfAGroupInFeedApi = {
    request: (groupId: number): GroupSharedHidePostsOfAGroupInFeedApiRequestAction => ({
        type: GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        response: any,
    ): GroupSharedHidePostsOfAGroupInFeedApiSuccessAction => ({
        type: GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_SUCCESS,
        groupId,
        response,
    }),
    failure: (
        groupId: number,
        error: string,
    ): GroupSharedHidePostsOfAGroupInFeedApiFailureAction => ({
        type: GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED =
    "GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED";

export interface GroupSharedHidePostsOfAGroupInFeedAction extends GroupSharedHidePostsOfAGroupInFeedApiAction {
    readonly type: typeof GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED;
    onSuccess();
}

export const groupSharedHidePostsOfAGroupInFeed = (
    groupId: number,
    onSuccess = null,
): GroupSharedHidePostsOfAGroupInFeedAction => ({
    type: GROUP_SHARED_HIDE_POSTS_OF_A_GROUP_IN_FEED,
    groupId,
    onSuccess,
});

/////////////////////////////////////////////////////////////////////
// Subscribe/unsubscribe group

export interface GroupSharedSubscribeGroupApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST = "GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST";

export interface GroupSharedSubscribeGroupApiRequestAction
    extends GroupSharedSubscribeGroupApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST;
}

export const GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS = "GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS";

export interface GroupSharedSubscribeGroupApiSuccessAction extends GroupSharedSubscribeGroupApiAction {
    readonly type: typeof GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS;
    readonly isSubscribed: boolean;
}

export const GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE = "GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE";

export interface GroupSharedSubscribeGroupApiFailureAction
    extends GroupSharedSubscribeGroupApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE;
}

export const groupSharedSubscribeGroupApi = {
    request: (groupId: number): GroupSharedSubscribeGroupApiRequestAction => ({
        type: GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        isSubscribed: boolean,
    ): GroupSharedSubscribeGroupApiSuccessAction => ({
        type: GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS,
        groupId,
        isSubscribed,
    }),
    failure: (groupId: number, error: string): GroupSharedSubscribeGroupApiFailureAction => ({
        type: GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_SUBSCRIBE_GROUP = "GROUP_SHARED_SUBSCRIBE_GROUP";

export interface GroupSharedSubscribeGroupAction extends GroupSharedSubscribeGroupApiAction {
    readonly type: typeof GROUP_SHARED_SUBSCRIBE_GROUP;
    readonly username: string;
    readonly subscribe: boolean;
}

export const groupSharedSubscribeGroup = (
    groupId: number,
    username: string,
    subscribe: boolean,
): GroupSharedSubscribeGroupAction => ({
    type: GROUP_SHARED_SUBSCRIBE_GROUP,
    groupId,
    username,
    subscribe,
});

/////////////////////////////////////////////////////////////////////
// Add or remove a group to/from list of favorite groups and topics

export interface GroupSharedSetGroupAsFavoriteApiAction extends Action {
    readonly groupId: number;
    readonly userId: number;
    readonly setAsFavorite: boolean;
}

export const GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_REQUEST =
    "GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_REQUEST";

export interface GroupSharedSetGroupAsFavoriteApiRequestAction
    extends GroupSharedSetGroupAsFavoriteApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_REQUEST;
}

export const GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_SUCCESS =
    "GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_SUCCESS";

export interface GroupSharedSetGroupAsFavoriteApiSuccessAction extends GroupSharedSetGroupAsFavoriteApiAction {
    readonly type: typeof GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_SUCCESS;
}

export const GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_FAILURE =
    "GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_FAILURE";

export interface GroupSharedSetGroupAsFavoriteApiFailureAction
    extends GroupSharedSetGroupAsFavoriteApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_FAILURE;
}

export const groupSharedSetGroupAsFavoriteApi = {
    request: (
        groupId: number,
        userId: number,
        setAsFavorite: boolean,
    ): GroupSharedSetGroupAsFavoriteApiRequestAction => ({
        type: GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_REQUEST,
        groupId,
        userId,
        setAsFavorite,
    }),
    success: (
        groupId: number,
        userId: number,
        setAsFavorite: boolean,
    ): GroupSharedSetGroupAsFavoriteApiSuccessAction => ({
        type: GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_SUCCESS,
        groupId,
        userId,
        setAsFavorite,
    }),
    failure: (
        groupId: number,
        userId: number,
        setAsFavorite: boolean,
        error: string,
    ): GroupSharedSetGroupAsFavoriteApiFailureAction => ({
        type: GROUP_SHARED_SET_GROUP_AS_FAVORITE_API_FAILURE,
        groupId,
        userId,
        setAsFavorite,
        error,
    }),
};

export const GROUP_SHARED_SET_GROUP_AS_FAVORITE = "GROUP_SHARED_SET_GROUP_AS_FAVORITE";

export interface GroupSharedSetGroupAsFavoriteAction extends GroupSharedSetGroupAsFavoriteApiAction {
    readonly type: typeof GROUP_SHARED_SET_GROUP_AS_FAVORITE;
    readonly userId: number;
    readonly setAsFavorite: boolean;
}

export const groupSharedSetGroupAsFavorite = (
    groupId: number,
    userId: number,
    setAsFavorite: boolean,
): GroupSharedSetGroupAsFavoriteAction => ({
    type: GROUP_SHARED_SET_GROUP_AS_FAVORITE,
    groupId,
    userId,
    setAsFavorite,
});

/////////////////////////////////////////////////////////////////////
// Delete group post (init)

export const GROUP_SHARED_DELETE_GROUP_POST_INIT = "GROUP_SHARED_DELETE_GROUP_POST_INIT";

export interface GroupSharedDeleteGroupPostInitAction extends Action {
    readonly type: typeof GROUP_SHARED_DELETE_GROUP_POST_INIT;
    readonly postId: number;
    readonly redirectUrl: string;
}

export const groupSharedDeleteGroupPostInit = (
    postId: number,
    redirectUrl?: string,
): GroupSharedDeleteGroupPostInitAction => ({
    type: GROUP_SHARED_DELETE_GROUP_POST_INIT,
    postId,
    redirectUrl,
});

/////////////////////////////////////////////////////////////////////
// Delete group post

export interface GroupSharedDeleteGroupPostApiAction extends Action {
    readonly postId: number;
}

export const GROUP_SHARED_DELETE_GROUP_POST_API_REQUEST =
    "GROUP_SHARED_DELETE_GROUP_POST_API_REQUEST";

export interface GroupSharedDeleteGroupPostApiRequestAction
    extends GroupSharedDeleteGroupPostApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_DELETE_GROUP_POST_API_REQUEST;
}

export const GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS =
    "GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS";

export interface GroupSharedDeleteGroupPostNormalizedResponse extends NormalizedResponse {
    readonly result: {
        post: number;
    };
}

export interface GroupSharedDeleteGroupPostApiSuccessAction
    extends
        GroupSharedDeleteGroupPostApiAction,
        APISuccessAction<GroupSharedDeleteGroupPostNormalizedResponse> {
    readonly type: typeof GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS;
}

export const GROUP_SHARED_DELETE_GROUP_POST_API_FAILURE =
    "GROUP_SHARED_DELETE_GROUP_POST_API_FAILURE";

export interface GroupSharedDeleteGroupPostApiFailureAction
    extends GroupSharedDeleteGroupPostApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_DELETE_GROUP_POST_API_FAILURE;
}

export const groupSharedDeleteGroupPostApi = {
    request: (postId: number): GroupSharedDeleteGroupPostApiRequestAction => ({
        type: GROUP_SHARED_DELETE_GROUP_POST_API_REQUEST,
        postId,
    }),
    success: (
        postId: number,
        response: GroupSharedDeleteGroupPostNormalizedResponse,
    ): GroupSharedDeleteGroupPostApiSuccessAction => ({
        type: GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS,
        postId,
        response,
    }),
    failure: (postId: number, error: string): GroupSharedDeleteGroupPostApiFailureAction => ({
        type: GROUP_SHARED_DELETE_GROUP_POST_API_FAILURE,
        postId,
        error,
    }),
};

export const GROUP_SHARED_DELETE_GROUP_POST = "GROUP_SHARED_DELETE_GROUP_POST";

export interface GroupSharedDeleteGroupPostAction extends GroupSharedDeleteGroupPostApiAction {
    readonly type: typeof GROUP_SHARED_DELETE_GROUP_POST;
    readonly redirectUrl: string;
    readonly comment: string;
}

export const groupSharedDeleteGroupPost = (
    postId: number,
    redirectUrl?: string,
    comment?: string,
): GroupSharedDeleteGroupPostAction => ({
    type: GROUP_SHARED_DELETE_GROUP_POST,
    postId,
    redirectUrl,
    comment,
});

/////////////////////////////////////////////////////////////////////
// Invite user(s) to group
export interface GroupSharedInviteUserApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_SHARED_INVITE_USER_API_REQUEST = "GROUP_SHARED_INVITE_USER_API_REQUEST";

export interface GroupSharedInviteUserApiRequestAction
    extends GroupSharedInviteUserApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_INVITE_USER_API_REQUEST;
}

export interface GroupSharedInviteUserNormalizedResponse extends NormalizedResponse {
    readonly result: { group: number };
}

export const GROUP_SHARED_INVITE_USER_API_SUCCESS = "GROUP_SHARED_INVITE_USER_API_SUCCESS";

export interface GroupSharedInviteUserApiSuccessAction
    extends
        GroupSharedInviteUserApiAction,
        APISuccessAction<GroupSharedInviteUserNormalizedResponse> {
    readonly type: typeof GROUP_SHARED_INVITE_USER_API_SUCCESS;
}

export const GROUP_SHARED_INVITE_USER_API_FAILURE = "GROUP_SHARED_INVITE_USER_API_FAILURE";

export interface GroupSharedInviteUserApiFailureAction
    extends GroupSharedInviteUserApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_INVITE_USER_API_FAILURE;
}

export const groupSharedInviteUserApi = {
    request: (groupId: number): GroupSharedInviteUserApiRequestAction => ({
        type: GROUP_SHARED_INVITE_USER_API_REQUEST,
        groupId,
    }),
    success: (
        groupId: number,
        response: GroupSharedInviteUserNormalizedResponse,
    ): GroupSharedInviteUserApiSuccessAction => ({
        type: GROUP_SHARED_INVITE_USER_API_SUCCESS,
        groupId,
        response,
    }),
    failure: (groupId: number, error: string): GroupSharedInviteUserApiFailureAction => ({
        type: GROUP_SHARED_INVITE_USER_API_FAILURE,
        groupId,
        error,
    }),
};

export const GROUP_SHARED_INVITE_USER = "GROUP_SHARED_INVITE_USER";

export interface GroupSharedInviteUserAction extends GroupSharedInviteUserApiAction {
    readonly type: typeof GROUP_SHARED_INVITE_USER;
}

export const groupSharedInviteUser = (groupId): GroupSharedInviteUserAction => ({
    type: GROUP_SHARED_INVITE_USER,
    groupId,
});

/////////////////////////////////////////////////////////////////////
// Post pin
export interface GroupSharedPostStickyApiAction extends Action {
    postId: number;
    sticky: boolean;
}

export const GROUP_SHARED_POST_STICKY_API_REQUEST = "GROUP_SHARED_POST_STICKY_API_REQUEST";

export interface GroupSharedPostStickyRequestAction
    extends GroupSharedPostStickyApiAction, APIRequestAction {
    readonly type: typeof GROUP_SHARED_POST_STICKY_API_REQUEST;
}

export const GROUP_SHARED_POST_STICKY_API_SUCCESS = "GROUP_SHARED_POST_STICKY_API_SUCCESS";

export interface GroupSharedPostStickySuccessAction
    extends GroupSharedPostStickyApiAction, APISuccessAction {
    readonly type: typeof GROUP_SHARED_POST_STICKY_API_SUCCESS;
}

export const GROUP_SHARED_POST_STICKY_API_FAILURE = "GROUP_SHARED_POST_STICKY_API_FAILURE";

export interface GroupSharedPostStickyFailureAction
    extends GroupSharedPostStickyApiAction, APIFailureAction {
    readonly type: typeof GROUP_SHARED_POST_STICKY_API_FAILURE;
}

export const groupSharedPostStickyApi = {
    request: (postId: number, sticky: boolean): GroupSharedPostStickyRequestAction => ({
        type: GROUP_SHARED_POST_STICKY_API_REQUEST,
        postId,
        sticky,
    }),
    success: (
        postId: number,
        sticky: boolean,
        response: any,
    ): GroupSharedPostStickySuccessAction => ({
        type: GROUP_SHARED_POST_STICKY_API_SUCCESS,
        postId,
        sticky,
        response,
    }),
    failure: (
        postId: number,
        sticky: boolean,
        error: string,
    ): GroupSharedPostStickyFailureAction => ({
        type: GROUP_SHARED_POST_STICKY_API_FAILURE,
        postId,
        sticky,
        error,
    }),
};

export const GROUP_SHARED_POST_STICKY = "GROUP_SHARED_POST_STICKY";

export interface GroupSharedPostStickyAction extends GroupSharedPostStickyApiAction {
    readonly type: typeof GROUP_SHARED_POST_STICKY;
}

export const groupSharedPostSticky = (
    postId: number,
    sticky: boolean,
): GroupSharedPostStickyAction => ({ type: GROUP_SHARED_POST_STICKY, postId, sticky });
